import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API } from "@/lib/api";
import { EntityPhoto } from "@/components/EntityPhoto";
import { formatRp, waLink, formatDateID, renderTemplate, DEFAULT_TEMPLATES, BOOKING_FIELD_MAP, calcAgeParts, calcPregnancy, CONSENT_TEXT } from "@/lib/constants";
import AddressPicker from "@/components/AddressPicker";
import { NearestSlots } from "@/components/NearestSlots";
import { PrivacyConsentBox, DataSafeNote, CONSENT_VERSION } from "@/components/PrivacyConsent";
import { Flower, CheckCircle, Baby, Heart, Sparkle, Stethoscope, Package, CaretLeft, Storefront, House, WhatsappLogo, Warning, Wrench, MagnifyingGlass, X, Plus, ChatsCircle } from "@phosphor-icons/react";

const inputCls = "w-full rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-sm outline-none transition-colors focus:border-rose-400 focus:ring-2 focus:ring-rose-100";
const labelCls = "mb-1.5 block text-xs font-semibold text-slate-600";

// Infer the type of dynamic questions from a category name
function catType(kat = "") {
  const k = kat.toLowerCase();
  if (k.includes("baby") || k.includes("bayi") || k.includes("anak")) return "baby";
  if (k.includes("kb")) return "kb";
  if (k.includes("yoga") || k.includes("kehamilan") || k.includes("hamil") || k.includes("bidan") || k.includes("konsultasi")) return "pregnancy";
  if (k.includes("spa")) return "spa";
  return "other";
}

function catIcon(kat = "") {
  const t = catType(kat);
  if (t === "baby") return Baby;
  if (t === "pregnancy") return Heart;
  if (t === "spa") return Sparkle;
  if (t === "kb") return Stethoscope;
  return Package;
}

const GERAI = "GBS Clinic / Gerai";
const HOMECARE = "Homecare";
const ONLINE = "Online";

// Kategori penemuan sebuah layanan: kategori utama + kategori tambahan (opsional).
const svcCats = (s) => [s.kategori, ...(Array.isArray(s.kategori_tambahan) ? s.kategori_tambahan : [])].filter(Boolean);
const inCategory = (s, catName) => svcCats(s).includes(catName);

export default function PublicBooking() {
  const [services, setServices] = useState([]);
  const [pubCats, setPubCats] = useState([]);
  const [staffList, setStaffList] = useState([]);
  const [settings, setSettings] = useState(null);
  const [avail, setAvail] = useState(null); // null | "loading" | {available, booked}
  const pendingJam = useRef("");
  const [schedInfo, setSchedInfo] = useState(null); // service schedule metadata
  const [svcSlots, setSvcSlots] = useState(null); // null | "loading" | {sessions}
  const [waUrl, setWaUrl] = useState("");
  const [step, setStep] = useState("category"); // category | service | form
  const [category, setCategory] = useState(null);
  const [service, setService] = useState(null);
  const [lokasi, setLokasi] = useState("");
  const [form, setForm] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");
  const [payInfo, setPayInfo] = useState(null);
  const [resId, setResId] = useState(null);
  const [showKetentuan, setShowKetentuan] = useState(false);
  const [policyOk, setPolicyOk] = useState(false);
  const [privacyOk, setPrivacyOk] = useState(false);
  const [payWa, setPayWa] = useState(null);
  const [tcText, setTcText] = useState("");
  const [homecareStaff, setHomecareStaff] = useState(null);
  const [practice, setPractice] = useState(null); // null | practice-availability payload (has_session)
  const [practiceResult, setPracticeResult] = useState(null); // final backend result for the success card
  const [bookingMeta, setBookingMeta] = useState(null); // mitra/tz snapshot for the success card
  const [svcSearch, setSvcSearch] = useState(""); // public service/category search (frontend-only)

  useEffect(() => {
    axios.get(`${API}/public/services`).then((r) => setServices(r.data)).catch(() => setServices([]));
    axios.get(`${API}/public/categories`).then((r) => setPubCats(r.data)).catch(() => setPubCats([]));
    axios.get(`${API}/public/staff`).then((r) => setStaffList(r.data)).catch(() => setStaffList([]));
    axios.get(`${API}/public/settings`).then((r) => setSettings(r.data)).catch(() => setSettings({}));
  }, []);

  const fixedMode = !!(schedInfo && schedInfo.mode && schedInfo.mode !== "staff");
  const showPetugas = !schedInfo || schedInfo.mode === "staff" || schedInfo.patient_chooses_staff;

  useEffect(() => {
    if (fixedMode) { setAvail(null); return; }
    // Any change to petugas/date/service/lokasi: clear old conflict error + previously chosen jam
    // and old slots before the new response arrives.
    setError("");
    setForm((f) => (f.jam_kunjungan ? { ...f, jam_kunjungan: "" } : f));
    // ROOT CAUSE FIX: never request (nor show fallback slots) until a valid service_id exists.
    // Previously this fired with service_id=undefined -> backend used a fallback duration, so slots
    // were shown that the final validation (real service duration + Homecare buffer) then rejected.
    if (!form.petugas_nama || !form.tanggal_kunjungan || !service?.id) { setAvail(null); return; }
    setAvail("loading");
    let ignore = false; // ignore stale (slow) responses so they can't overwrite the latest request
    axios.get(`${API}/public/availability`, { params: { petugas: form.petugas_nama, date: form.tanggal_kunjungan, service_id: service.id, lokasi } })
      .then((r) => {
        if (ignore) return;
        setAvail(r.data);
        const pj = pendingJam.current;
        pendingJam.current = "";
        if (pj && (r.data.available || []).includes(pj)) setForm((f) => ({ ...f, jam_kunjungan: pj }));
      })
      .catch(() => { if (!ignore) setAvail({ available: [], booked: [] }); });
    return () => { ignore = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.petugas_nama, form.tanggal_kunjungan, service?.id, lokasi, fixedMode]);

  // Homecare: filter eligible staff by patient's kelurahan (service area per staff)
  useEffect(() => {
    if (lokasi !== HOMECARE || !service?.id || !form.kelurahan) { setHomecareStaff(null); return; }
    axios.get(`${API}/public/homecare-staff`, { params: { service_id: service.id, kelurahan: form.kelurahan } })
      .then((r) => {
        setHomecareStaff(r.data);
        if (form.petugas_nama && !r.data.some((s) => s.nama === form.petugas_nama)) setForm((f) => ({ ...f, petugas_nama: "", jam_kunjungan: "" }));
      })
      .catch(() => setHomecareStaff([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lokasi, service?.id, form.kelurahan]);

  // Practice/queue session (Gerai, single service, non-fixed): patient does NOT pick a time.
  useEffect(() => {
    if (fixedMode || lokasi === HOMECARE || lokasi === ONLINE || !form.petugas_nama || !form.tanggal_kunjungan || !service?.id) { setPractice(null); return; }
    axios.get(`${API}/public/practice-availability`, { params: { petugas: form.petugas_nama, service_id: service.id, date: form.tanggal_kunjungan } })
      .then((r) => setPractice(r.data && r.data.has_session ? r.data : null))
      .catch(() => setPractice(null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.petugas_nama, form.tanggal_kunjungan, service?.id, fixedMode, lokasi]);

  // Auto-fill HPL from HPHT (editable afterwards)
  useEffect(() => {
    const p = calcPregnancy(form.hpht, form.tanggal_kunjungan);
    if (p && p.hpl && !form.hpl) setForm((f) => ({ ...f, hpl: p.hpl }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.hpht]);

  useEffect(() => {
    if (!fixedMode || !service?.id || !form.tanggal_kunjungan) { setSvcSlots(null); return; }
    setSvcSlots("loading");
    axios.get(`${API}/public/service-slots`, { params: { service_id: service.id, date: form.tanggal_kunjungan } })
      .then((r) => {
        setSvcSlots(r.data);
        const sessions = r.data.sessions || [];
        const curOk = sessions.some((x) => x.time === form.jam_kunjungan && !x.full);
        const avail = sessions.filter((x) => !x.full);
        // Keep valid selection; otherwise clear stale one and auto-select when exactly ONE available session (class schedules)
        if (!curOk) setForm((f) => ({ ...f, jam_kunjungan: avail.length === 1 ? avail[0].time : "" }));
      })
      .catch(() => setSvcSlots({ sessions: [] }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [service?.id, form.tanggal_kunjungan, fixedMode]);

  const categories = useMemo(() => {
    const map = {};
    services.forEach((s) => { svcCats(s).forEach((k) => { map[k] = (map[k] || 0) + 1; }); });
    if (pubCats.length) {
      return pubCats.filter((c) => map[c.nama]).map((c) => ({ nama: c.nama, count: map[c.nama] }));
    }
    return Object.entries(map).map(([nama, count]) => ({ nama, count }));
  }, [services, pubCats]);

  const catServices = useMemo(() => services.filter((s) => inCategory(s, category)), [services, category]);

  // Frontend-only live search. Kategori match -> tampilkan kategori; layanan match -> layanan cocok.
  // Daftar gabungan hasil pencarian di-dedupe by service id agar layanan multi-kategori muncul sekali.
  const searchGroups = useMemo(() => {
    const q = svcSearch.trim().toLowerCase();
    if (!q) return null;
    const seen = new Set();
    const groups = [];
    categories.forEach((c) => {
      const inCat = services.filter((s) => inCategory(s, c.nama));
      const catHit = c.nama.toLowerCase().includes(q);
      const pool = catHit ? inCat : inCat.filter((s) => (s.nama || "").toLowerCase().includes(q));
      const uniq = pool.filter((s) => { const id = s.id || s._id || s.nama; if (seen.has(id)) return false; seen.add(id); return true; });
      if (uniq.length) groups.push({ nama: c.nama, services: uniq });
    });
    return groups;
  }, [svcSearch, services, categories]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const chooseCategory = (c) => { setCategory(c.nama); setStep("service"); };
  const chooseService = (s) => {
    setService(s);
    setSchedInfo(null); setSvcSlots(null);
    axios.get(`${API}/public/service-schedule-info`, { params: { service_id: s.id } }).then((r) => setSchedInfo(r.data)).catch(() => setSchedInfo({ mode: "staff", patient_chooses_staff: true }));
    const canGerai = s.di_gerai !== false;
    const canHome = !!s.bisa_homecare;
    setLokasi(canGerai ? GERAI : canHome ? HOMECARE : s.bisa_online ? ONLINE : GERAI);
    if (s.bisa_online && !tcText) axios.get(`${API}/consult/consent-text`).then((r) => setTcText(r.data.text)).catch(() => {});
    setForm({});
    setStep("form");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // ?service=<id> (dari Pesan Beberapa Layanan) -> langsung ke layanan itu.
  const [deepDone, setDeepDone] = useState(false);
  useEffect(() => {
    const sid = new URLSearchParams(window.location.search).get("service");
    if (deepDone || !sid || !services?.length) return;
    const s = services.find((x) => x.id === sid);
    setDeepDone(true);
    if (s) { setCategory(s.kategori || null); chooseService(s); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [services, deepDone]);

  const back = () => {
    setError("");
    if (step === "form") { setStep("service"); setService(null); }
    else if (step === "service") { setStep("category"); setCategory(null); }
  };

  const priceForLocation = () => {
    if (lokasi === HOMECARE && service?.harga_homecare != null) return service.harga_homecare;
    return service?.harga_normal || 0;
  };

  const isHome = lokasi === HOMECARE;
  const effectiveFields = service?.booking_fields || [];
  const isImunService = effectiveFields.includes("jenis_vaksin") || effectiveFields.includes("nama_bayi");
  const requiredSet = new Set(service?.required_fields || []);
  const isReq = (key) => (service?.required_fields != null ? requiredSet.has(key) : !!BOOKING_FIELD_MAP[key]?.required);
  const preg = calcPregnancy(form.hpht, form.tanggal_kunjungan);

  const previewTotal = () => {
    if (isHome && service?.harga_homecare != null) return service.harga_homecare;
    const p = service?.harga_promo;
    const base = service?.harga_normal || 0;
    if (p != null && p !== "" && Number(p) > 0) {
      const d = form.tanggal_kunjungan || new Date().toISOString().slice(0, 10);
      const s = service.promo_start, e = service.promo_end;
      if ((!s || d >= s) && (!e || d <= e)) return Number(p);
    }
    return base;
  };
  const previewDp = () => {
    const t = previewTotal();
    const m = service?.payment_mode;
    if (m === "full") return t;
    if (m === "dp") return service.dp_type === "persen" ? Math.round(t * Number(service.dp_value || 0) / 100) : Math.round(Number(service.dp_value || 0));
    return 0;
  };

  const submit = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    proceed(false);
  };

  const proceed = async (accepted) => {
    setError("");
    if (!form.nama_lengkap || !form.no_whatsapp) return setError("Nama dan Nomor WhatsApp wajib diisi.");
    if (showPetugas && !form.petugas_nama) return setError("Silakan pilih petugas/bidan.");
    if (!form.tanggal_kunjungan) return setError("Silakan pilih tanggal kunjungan.");
    if (practice && practice.can_book === false) return setError("Kuota sesi praktik pada tanggal ini sudah penuh. Silakan pilih tanggal lain.");
    if (!practice && !form.jam_kunjungan) return setError("Silakan pilih jam kunjungan.");
    if (isHome && !form.alamat) return setError("Alamat lengkap wajib diisi untuk layanan homecare.");
    if (lokasi === ONLINE && !form.tc_consent) return setError("Persetujuan telekonsultasi wajib dicentang untuk Konsultasi Online.");
    if (effectiveFields.includes("consent_imunisasi") && !form.consent_imunisasi) return setError("Anda harus menyetujui pernyataan persetujuan imunisasi.");
    for (const key of effectiveFields) {
      const f = BOOKING_FIELD_MAP[key];
      if (f && isReq(key) && !form[key]) return setError(`${f.label} wajib diisi.`);
    }
    if (effectiveFields.includes("nik_anak") && form.nik_anak && !/^\d{16}$/.test(form.nik_anak)) return setError("NIK Anak harus tepat 16 digit angka.");

    const mode = service?.payment_mode || "none";
    if (mode !== "none" && !accepted && !policyOk) { setShowKetentuan(true); return; }
    const toNum = (v) => (v === "" || v == null ? null : Number(v));
    const dobForAge = form.tanggal_lahir || form.tanggal_lahir_bayi;
    const ageP = calcAgeParts(dobForAge);
    const payload = {
      nama_lengkap: form.nama_lengkap,
      no_whatsapp: form.no_whatsapp,
      ...Object.fromEntries(effectiveFields.map((k) => [k, form[k]])),
      consent_accepted: !!form.consent_imunisasi,
      consent_at: form.consent_imunisasi ? new Date().toISOString() : null,
      consent_text: effectiveFields.includes("consent_imunisasi") ? CONSENT_TEXT : null,
      nik: form.nik || "",
      tanggal_lahir: form.tanggal_lahir || null,
      jenis_kelamin: form.jenis_kelamin || "",
      hpht: form.hpht || null,
      kehamilan_ke: toNum(form.kehamilan_ke),
      usia_kehamilan_minggu: (preg && form.hpht) ? preg.weeks : toNum(form.usia_kehamilan_minggu),
      usia_kehamilan_hari: (preg && form.hpht) ? preg.days : null,
      usia_kehamilan_text: (preg && form.hpht) ? preg.ukText : "",
      hpl: form.hpl || (preg && form.hpht ? preg.hpl : null),
      // computed age snapshot (persisted so reports/detail don't need to recalc)
      usia: ageP ? ageP.y : toNum(form.usia),
      usia_text: ageP ? ageP.text : "",
      keluhan: form.keluhan || "",
      tanggal_kunjungan: form.tanggal_kunjungan,
      jam_kunjungan: practice ? "" : form.jam_kunjungan,
      // service snapshot (single source of truth = Master Layanan)
      jenis_layanan: service.nama,
      kategori_layanan: service.kategori,
      service_id: service.id,
      petugas_nama: form.petugas_nama || "",
      lokasi,
      telekonsultasi_consent: lokasi === ONLINE && !!form.tc_consent,
      harga_normal: priceForLocation(),
      // baby / mom fields
      nama_bayi: form.nama_bayi || "",
      tanggal_lahir_bayi: form.tanggal_lahir_bayi || null,
      jenis_kelamin_bayi: form.jenis_kelamin_bayi || "",
      nama_ibu: form.nama_ibu || "",
      // structured address (homecare + services that enable address)
      alamat: form.alamat || "",
      provinsi: form.provinsi || "", kabupaten: form.kabupaten || "",
      kecamatan: form.kecamatan || "", kelurahan: form.kelurahan || "",
      jalan: form.jalan || "", no_rumah: form.no_rumah || "", rt: form.rt || "", rw: form.rw || "", kode_pos: form.kode_pos || "",
      patokan: form.patokan || "",
      maps_link: form.maps_link || "",
      alamat_data: form.alamat_data || null,
      policy_accepted: (service?.payment_mode || "none") !== "none",
      policy_accepted_at: new Date().toISOString(),
      consent_privacy: privacyOk,
      consent_version: CONSENT_VERSION,
    };
    setSubmitting(true);
    try {
      const resp = await axios.post(`${API}${practice ? "/public/practice-reservations" : "/public/reservations"}`, payload);
      setResId(resp.data.reservation_id);
      setPayInfo(resp.data.payment || { mode: "none" });
      setBookingMeta({
        is_mitra: !!resp.data.is_mitra,
        mitra_kota: resp.data.mitra_kota || "",
        tz_label: resp.data.tz_label || "",
        petugas: payload.petugas_nama,
        jam: practice ? "" : payload.jam_kunjungan,
        tanggal: payload.tanggal_kunjungan,
        layanan: service.nama,
      });
      if (practice) {
        setPracticeResult({
          queue_number: resp.data.queue_number,
          estimated_time: resp.data.estimated_time,
          window: resp.data.window,
          tanggal: payload.tanggal_kunjungan,
          petugas: payload.petugas_nama,
          layanan: service.nama,
        });
      } else {
        setPracticeResult(null);
      }
      const jamDisplay = practice ? `Antrean #${resp.data.queue_number} · perkiraan ${resp.data.estimated_time} (Praktik ${resp.data.window})` : payload.jam_kunjungan;
      const wa = settings?.clinic_wa;
      const isPaid = mode === "dp" || mode === "full";
      setPayWa({ clinic_wa: wa, nama: payload.nama_lengkap, layanan: service.nama, tanggal: formatDateID(payload.tanggal_kunjungan), jam: jamDisplay });
      if (wa && !isPaid) {
        // Non-paid services: confirmation WhatsApp is available immediately.
        const msg = renderTemplate((settings && settings.tpl_confirm) || DEFAULT_TEMPLATES.tpl_confirm, {
          nama: payload.nama_lengkap,
          layanan: service.nama,
          kategori: service.kategori,
          tanggal: formatDateID(payload.tanggal_kunjungan),
          jam: jamDisplay,
          lokasi,
          petugas: payload.petugas_nama,
        });
        const url = waLink(wa, msg);
        setWaUrl(url);
        setTimeout(() => window.open(url, "_blank"), 400);
      }
      setDone(true);
      setShowKetentuan(false);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e2) {
      if (practice && e2?.response?.status === 409) {
        setError("Kuota sesi praktik pada tanggal ini sudah penuh. Silakan pilih tanggal lain.");
      } else {
        setError(e2?.response?.data?.detail || "Gagal mengirim reservasi. Silakan coba lagi.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const resetAll = () => { setDone(false); setStep("category"); setCategory(null); setService(null); setForm({}); setAvail(null); setWaUrl(""); setPolicyOk(false); setPrivacyOk(false); setShowKetentuan(false); setPayWa(null); setPracticeResult(null); };

  return (
    <div className="min-h-screen bg-[#FAF7FF]">
      <header className="border-b border-rose-100/70 bg-white">
        <div className="mx-auto flex max-w-3xl items-center gap-3 px-4 py-4 sm:px-6">
          <img src={`${process.env.PUBLIC_URL}/logo.webp`} alt="GBS Clinic & Birth Center" className="h-11 w-auto object-contain" />
          <div className="leading-tight">
            <p className="font-heading text-sm font-extrabold text-slate-800">Reservasi GBS Clinic</p>
            <p className="text-xs text-slate-400">Reservation &amp; Service Management</p>
          </div>
          <div className="ml-auto">
            <Link to="/login" className="text-sm font-semibold text-[#DB2777] hover:underline" data-testid="public-login-link">Login Admin</Link>
          </div>
        </div>
        <div className="mx-auto max-w-3xl px-4 pb-3 sm:px-6">
          <Link to="/artikel" data-testid="booking-articles-link" className="inline-flex items-center gap-1.5 rounded-full border border-[#E5D9FF] bg-[#F6F0FF] px-3 py-1 text-xs font-bold text-[#7C4DE0] hover:bg-[#EEE6FF]">Artikel &amp; Edukasi GBS</Link>
        </div>
      </header>

      {settings?.booking_locked ? (
        <main className="mx-auto max-w-3xl px-4 py-16 sm:px-6">
          <div data-testid="booking-maintenance" className="rounded-2xl border border-amber-200 bg-amber-50 p-8 text-center">
            <Wrench size={52} weight="duotone" className="mx-auto text-amber-600" />
            <h1 className="mt-3 font-heading text-2xl font-extrabold text-slate-800">Reservasi Online Sedang Dipelihara</h1>
            <p className="mt-2 text-slate-600">Reservasi online sedang dalam pemeliharaan singkat. Silakan hubungi Admin GBS untuk reservasi.</p>
            {settings?.clinic_wa && (
              <a data-testid="maintenance-wa" href={`https://wa.me/${String(settings.clinic_wa).replace(/[^0-9]/g, "")}`} target="_blank" rel="noreferrer" className="mt-5 inline-flex items-center gap-2 rounded-xl bg-emerald-500 px-6 py-2.5 text-sm font-bold text-white hover:bg-emerald-600"><WhatsappLogo size={18} weight="fill" /> Hubungi Admin GBS</a>
            )}
          </div>
        </main>
      ) : (
      <main className="mx-auto max-w-3xl px-4 py-8 sm:px-6">
        {done ? (
          <div data-testid="booking-success" className="rounded-2xl border border-emerald-200 bg-emerald-50 p-8 text-center">
            <CheckCircle size={56} weight="duotone" className="mx-auto text-emerald-600" />
            <h1 className="mt-3 font-heading text-2xl font-extrabold text-slate-800">Reservasi Terkirim!</h1>
            <p className="mt-2 text-slate-600">Terima kasih. Reservasi Anda telah kami terima. {lokasi === ONLINE && <span data-testid="booking-success-online">Ini adalah Konsultasi Online: tautan ruang konsultasi akan dikirim via WhatsApp setelah reservasi dikonfirmasi{payInfo && (payInfo.mode === "dp" || payInfo.mode === "full") ? " dan pembayaran terverifikasi" : ""}. </span>}{payInfo && (payInfo.mode === "dp" || payInfo.mode === "full") ? "Silakan selesaikan pembayaran di bawah agar reservasi dikonfirmasi." : "Admin akan menghubungi Anda melalui WhatsApp untuk konfirmasi jadwal."}</p>
            {practiceResult && (
              <div data-testid="booking-success-practice" className="mx-auto mt-4 max-w-sm rounded-xl border border-sky-200 bg-white p-4 text-left text-sm">
                <p data-testid="success-queue-number" className="mb-2 text-center font-heading text-xl font-extrabold text-sky-700">Nomor Antrean Anda: #{practiceResult.queue_number}</p>
                <ul className="space-y-1 text-slate-600">
                  <li data-testid="success-estimated-time"><span className="font-semibold">Perkiraan jam pelayanan:</span> {practiceResult.estimated_time}</li>
                  <li><span className="font-semibold">Rentang sesi praktik:</span> {practiceResult.window}</li>
                  <li><span className="font-semibold">Tanggal:</span> {formatDateID(practiceResult.tanggal)}</li>
                  <li><span className="font-semibold">Petugas:</span> {practiceResult.petugas}</li>
                  <li><span className="font-semibold">Layanan:</span> {practiceResult.layanan}</li>
                </ul>
                <p className="mt-2 text-xs text-slate-400">Nomor antrean &amp; perkiraan jam ditentukan otomatis oleh sistem.</p>
              </div>
            )}
            {bookingMeta && bookingMeta.is_mitra && (
              <div data-testid="booking-success-mitra" className="mx-auto mt-4 max-w-sm rounded-xl border border-violet-200 bg-white p-4 text-left text-sm">
                <p data-testid="success-mitra-label" className="mb-2 text-center font-heading text-base font-extrabold text-violet-700">Mitra MOG{bookingMeta.mitra_kota ? ` — ${bookingMeta.mitra_kota}` : ""}</p>
                <ul className="space-y-1 text-slate-600">
                  <li data-testid="success-mitra-petugas"><span className="font-semibold">Praktisi pelaksana:</span> {bookingMeta.petugas}</li>
                  <li><span className="font-semibold">Layanan:</span> {bookingMeta.layanan}</li>
                  <li><span className="font-semibold">Tanggal:</span> {formatDateID(bookingMeta.tanggal)}</li>
                  {bookingMeta.jam && <li><span className="font-semibold">Jam:</span> {bookingMeta.jam}{bookingMeta.tz_label ? ` ${bookingMeta.tz_label}` : ""}</li>}
                </ul>
                <p className="mt-2 text-xs text-slate-400">Layanan Homecare oleh Mitra GBS. Pembayaran tetap melalui GBS Clinic.</p>
              </div>
            )}
            <div className="mt-5 flex flex-wrap items-center justify-center gap-2">
              {waUrl && <a data-testid="booking-wa" href={waUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 rounded-xl bg-emerald-500 px-6 py-2.5 text-sm font-bold text-white hover:bg-emerald-600"><WhatsappLogo size={18} weight="fill" /> Konfirmasi via WhatsApp</a>}
              <button data-testid="booking-again" onClick={resetAll} className="rounded-xl bg-[#EC4899] px-6 py-2.5 text-sm font-bold text-white hover:bg-[#DB2777]">Buat Reservasi Lagi</button>
            </div>
            {payInfo && payInfo.mode && payInfo.mode !== "none" && resId && (
              <PaymentPanel resId={resId} payInfo={payInfo} waInfo={payWa} />
            )}
          </div>
        ) : (
          <>
            {step !== "category" && (
              <button data-testid="booking-back" onClick={back} className="mb-4 inline-flex items-center gap-1 text-sm font-semibold text-[#DB2777] hover:underline"><CaretLeft size={16} weight="bold" /> Kembali</button>
            )}

            {/* STEP 1: Categories */}
            {step === "category" && (
              <div data-testid="step-category">
                <h1 className="font-heading text-3xl font-extrabold tracking-tight text-slate-800">Pilih Layanan</h1>
                <p className="mt-1 text-sm text-slate-500">Pilih kategori layanan yang Anda inginkan untuk memulai reservasi.</p>
                <Link to="/konsultasi-sekarang" data-testid="pb-consult-online-shortcut" className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-[#9333EA] hover:underline"><ChatsCircle size={14} weight="duotone" /> Chat Dokter &amp; Bidan Online</Link>

                <div className="relative mt-4">
                  <MagnifyingGlass size={18} weight="bold" className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input
                    data-testid="public-service-search"
                    type="text"
                    value={svcSearch}
                    onChange={(e) => setSvcSearch(e.target.value)}
                    placeholder="Cari layanan, misalnya pijat bayi, yoga, USG…"
                    aria-label="Cari layanan"
                    className="w-full rounded-2xl border border-rose-100/70 bg-white py-3 pl-11 pr-11 text-sm text-slate-700 shadow-sm outline-none transition-colors placeholder:text-slate-400 focus:border-[#EC4899] focus:ring-2 focus:ring-[#FCE7F3]"
                  />
                  {svcSearch && (
                    <button
                      type="button"
                      data-testid="public-service-search-clear"
                      onClick={() => setSvcSearch("")}
                      aria-label="Hapus pencarian"
                      className="absolute right-3 top-1/2 flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded-full text-slate-400 transition-colors hover:bg-rose-50 hover:text-[#DB2777]"
                    >
                      <X size={16} weight="bold" />
                    </button>
                  )}
                </div>

                {/* Multi-service booking banner (moved from header). Routes to existing /booking-multi flow. */}
                <Link
                  to="/booking-multi"
                  data-testid="multi-booking-link"
                  className="mt-4 flex flex-col gap-3 rounded-2xl border border-[#E4D7FB] bg-[#F5F0FE] px-4 py-3 transition-all hover:border-[#7C4DE0] hover:bg-[#EFE7FD] active:scale-[0.99] sm:flex-row sm:items-center"
                >
                  <div className="flex min-w-0 flex-1 items-center gap-3">
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#7C4DE0] text-white">
                      <Plus size={22} weight="bold" />
                    </span>
                    <div className="min-w-0">
                      <p className="font-heading text-sm font-bold text-[#5B29B3]">Butuh lebih dari satu layanan?</p>
                      <p className="text-xs text-slate-500">Pesan beberapa layanan sekaligus dalam satu reservasi.</p>
                    </div>
                  </div>
                  <span className="w-full shrink-0 rounded-xl bg-[#7C4DE0] px-4 py-2.5 text-center text-xs font-bold text-white transition-colors sm:w-auto">
                    Pesan Beberapa Layanan
                  </span>
                </Link>

                {/* Default: category grid (no keyword) */}
                {!searchGroups && (
                  <div className="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-2">
                    {categories.length === 0 && <p className="text-sm text-slate-400">Belum ada layanan tersedia.</p>}
                    {categories.map((c) => {
                      const Icon = catIcon(c.nama);
                      return (
                        <button key={c.nama} data-testid={`category-card-${c.nama}`} onClick={() => chooseCategory(c)} className="group flex items-center gap-4 rounded-2xl border border-rose-100/70 bg-white p-5 text-left shadow-sm transition-all hover:border-[#EC4899] hover:shadow-md active:scale-[0.98]">
                          <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-[#FCE7F3] text-[#DB2777] transition-colors group-hover:bg-[#EC4899] group-hover:text-white"><Icon size={26} weight="duotone" /></span>
                          <div>
                            <p className="font-heading font-bold text-slate-800">{c.nama}</p>
                            <p className="text-xs text-slate-400">{c.count} layanan</p>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}

                {/* Search results: matching categories + their matching services */}
                {searchGroups && searchGroups.length > 0 && (
                  <div className="mt-6 space-y-6" data-testid="public-service-search-results">
                    {searchGroups.map((g) => {
                      const Icon = catIcon(g.nama);
                      return (
                        <div key={g.nama} data-testid={`search-cat-${g.nama}`}>
                          <div className="mb-2 flex items-center gap-2">
                            <span className="flex h-8 w-8 items-center justify-center rounded-xl bg-[#FCE7F3] text-[#DB2777]"><Icon size={18} weight="duotone" /></span>
                            <p className="font-heading text-sm font-bold text-slate-700">{g.nama}</p>
                          </div>
                          <div className="space-y-3">
                            {g.services.map((s) => (
                              <button key={s.id} data-testid={`service-card-${s.id}`} onClick={() => chooseService(s)} className="flex w-full items-center justify-between gap-3 rounded-2xl border border-rose-100/70 bg-white p-5 text-left shadow-sm transition-all hover:border-[#EC4899] hover:shadow-md active:scale-[0.99]">
                                <EntityPhoto src={s.foto_file_id ? `${API}/public/services/${s.id}/foto` : null} name={s.nama} className="h-12 w-12 shrink-0" />
                                <div>
                                  <p className="font-heading font-bold text-slate-800">{s.nama}</p>
                                  {s.deskripsi && <p className="mt-0.5 text-xs text-slate-400">{s.deskripsi}</p>}
                                  <div className="mt-1.5 flex flex-wrap gap-1.5 text-xs">
                                    {s.di_gerai !== false && <span className="inline-flex items-center gap-1 rounded-full bg-sky-50 px-2 py-0.5 font-semibold text-sky-700"><Storefront size={11} /> Gerai</span>}
                                    {s.bisa_homecare && <span className="inline-flex items-center gap-1 rounded-full bg-purple-50 px-2 py-0.5 font-semibold text-purple-700"><House size={11} /> Homecare</span>}
{s.bisa_online && <span className="inline-flex items-center gap-1 rounded-full bg-teal-50 px-2 py-0.5 font-semibold text-teal-700"><ChatsCircle size={11} /> Online</span>}
                                    {s.durasi ? <span className="rounded-full bg-slate-100 px-2 py-0.5 font-semibold text-slate-500">{s.durasi} mnt</span> : null}
                                  </div>
                                </div>
                                <span className="shrink-0 font-heading text-base font-extrabold text-[#DB2777]">{formatRp(s.harga_normal)}</span>
                              </button>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* No results */}
                {searchGroups && searchGroups.length === 0 && (
                  <p data-testid="public-service-no-results" className="mt-8 rounded-2xl bg-slate-50 px-4 py-6 text-center text-sm text-slate-500">
                    Layanan yang Anda cari belum ditemukan. Coba gunakan kata kunci lain.
                  </p>
                )}
              </div>
            )}

            {/* STEP 2: Services in category */}
            {step === "service" && (
              <div data-testid="step-service">
                <h1 className="font-heading text-2xl font-extrabold text-slate-800">{category}</h1>
                <p className="mt-1 text-sm text-slate-500">Pilih layanan yang diinginkan.</p>
                <div className="mt-6 space-y-3">
                  {catServices.map((s) => (
                    <button key={s.id} data-testid={`service-card-${s.id}`} onClick={() => chooseService(s)} className="flex w-full items-center justify-between gap-3 rounded-2xl border border-rose-100/70 bg-white p-5 text-left shadow-sm transition-all hover:border-[#EC4899] hover:shadow-md active:scale-[0.99]">
                      <EntityPhoto src={s.foto_file_id ? `${API}/public/services/${s.id}/foto` : null} name={s.nama} className="h-12 w-12 shrink-0" />
                      <div>
                        <p className="font-heading font-bold text-slate-800">{s.nama}</p>
                        {s.deskripsi && <p className="mt-0.5 text-xs text-slate-400">{s.deskripsi}</p>}
                        <div className="mt-1.5 flex flex-wrap gap-1.5 text-xs">
                          {s.di_gerai !== false && <span className="inline-flex items-center gap-1 rounded-full bg-sky-50 px-2 py-0.5 font-semibold text-sky-700"><Storefront size={11} /> Gerai</span>}
                          {s.bisa_homecare && <span className="inline-flex items-center gap-1 rounded-full bg-purple-50 px-2 py-0.5 font-semibold text-purple-700"><House size={11} /> Homecare</span>}
{s.bisa_online && <span className="inline-flex items-center gap-1 rounded-full bg-teal-50 px-2 py-0.5 font-semibold text-teal-700"><ChatsCircle size={11} /> Online</span>}
                          {s.durasi ? <span className="rounded-full bg-slate-100 px-2 py-0.5 font-semibold text-slate-500">{s.durasi} mnt</span> : null}
                        </div>
                      </div>
                      <span className="shrink-0 font-heading text-base font-extrabold text-[#DB2777]">{formatRp(s.harga_normal)}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* STEP 3: Dynamic form */}
            {step === "form" && service && (
              <form onSubmit={submit} className="space-y-5" data-testid="public-reservation-form">
                <div className="rounded-2xl border border-rose-100/70 bg-white p-5">
                  <p className="text-xs font-semibold text-slate-400">Layanan dipilih</p>
                  <p className="font-heading text-lg font-extrabold text-slate-800">{service.nama}</p>
                  <p className="text-sm text-[#DB2777]">{formatRp(priceForLocation())}</p>
                </div>

                {/* Location choice */}
                {[service.di_gerai !== false, !!service.bisa_homecare, !!service.bisa_online].filter(Boolean).length > 1 && (
                  <div className="rounded-2xl border border-rose-100/70 bg-white p-5">
                    <label className={labelCls}>Lokasi Layanan *</label>
                    <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                      {service.di_gerai !== false && <button type="button" data-testid="loc-gerai" onClick={() => setLokasi(GERAI)} className={`flex items-center justify-center gap-2 rounded-xl border px-3 py-3 text-sm font-semibold ${lokasi === GERAI ? "border-[#EC4899] bg-[#FCE7F3] text-[#DB2777]" : "border-slate-200 text-slate-500"}`}><Storefront size={18} /> Di Gerai/Klinik</button>}
                      {service.bisa_homecare && <button type="button" data-testid="loc-homecare" onClick={() => setLokasi(HOMECARE)} className={`flex items-center justify-center gap-2 rounded-xl border px-3 py-3 text-sm font-semibold ${lokasi === HOMECARE ? "border-[#EC4899] bg-[#FCE7F3] text-[#DB2777]" : "border-slate-200 text-slate-500"}`}><House size={18} /> Homecare</button>}
                      {service.bisa_online && <button type="button" data-testid="loc-online" onClick={() => setLokasi(ONLINE)} className={`flex items-center justify-center gap-2 rounded-xl border px-3 py-3 text-sm font-semibold ${lokasi === ONLINE ? "border-[#EC4899] bg-[#FCE7F3] text-[#DB2777]" : "border-slate-200 text-slate-500"}`}><ChatsCircle size={18} /> Konsultasi Online</button>}
                    </div>
                  </div>
                )}
                {lokasi === ONLINE && (
                  <div className="rounded-2xl border border-teal-200 bg-teal-50 p-5" data-testid="pb-online-info">
                    <p className="mb-2 flex items-center gap-2 text-sm font-bold text-teal-800"><ChatsCircle size={18} /> Konsultasi Online (chat terjadwal)</p>
                    <p className="mb-3 text-xs text-teal-700">Tanpa biaya transport. Tautan ruang konsultasi dikirim via WhatsApp setelah reservasi dikonfirmasi; sesi dimulai oleh tenaga kesehatan sesuai jadwal.</p>
                    {service.bisa_sekarang && <Link to="/konsultasi-sekarang" data-testid="pb-consult-now-link" className="mb-3 inline-block rounded-full bg-emerald-600 px-3 py-1.5 text-xs font-bold text-white">Ingin langsung? Konsultasi Sekarang dengan dokter yang tersedia</Link>}
                    <label className="flex items-start gap-2 text-xs text-slate-700">
                      <input type="checkbox" data-testid="pb-online-consent" checked={!!form.tc_consent} onChange={(e) => set("tc_consent", e.target.checked)} className="mt-0.5" />
                      <span data-testid="pb-online-consent-text">{tcText || "Saya menyetujui layanan konsultasi online (telekonsultasi) melalui ruang chat resmi GBS."} *</span>
                    </label>
                  </div>
                )}

                {/* Patient data */}
                <div className="rounded-2xl border border-rose-100/70 bg-white p-5">
                  <h3 className="mb-4 font-heading text-base font-bold text-slate-800">Data Diri</h3>
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <div className="sm:col-span-2"><label className={labelCls}>Nama Lengkap *</label><input data-testid="pb-nama" required value={form.nama_lengkap || ""} onChange={(e) => set("nama_lengkap", e.target.value)} className={inputCls} placeholder="Nama Anda" /></div>
                    <div><label className={labelCls}>Nomor WhatsApp *</label><input data-testid="pb-wa" required value={form.no_whatsapp || ""} onChange={(e) => set("no_whatsapp", e.target.value)} className={inputCls} placeholder="0812xxxxxxxx" /></div>
                    <div><label className={labelCls}>{isImunService ? "NIK Orang Tua / Wali" : "NIK"}</label><input data-testid="pb-nik" value={form.nik || ""} onChange={(e) => set("nik", e.target.value)} className={inputCls} placeholder={isImunService ? "16 digit NIK orang tua/wali (opsional)" : "16 digit NIK (opsional)"} /></div>
                  </div>
                  <DataSafeNote />
                </div>

                {/* Dynamic per-service booking fields */}
                {effectiveFields.length > 0 && (
                  <div className="rounded-2xl border border-rose-100/70 bg-white p-5" data-testid="fields-dynamic">
                    <h3 className="mb-4 font-heading text-base font-bold text-slate-800">Data Tambahan</h3>
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                      {effectiveFields.map((key) => {
                        const f = BOOKING_FIELD_MAP[key];
                        if (!f) return null;
                        if (f.type === "address") {
                          return (
                            <div key={key} className="sm:col-span-2">
                              <label className={labelCls}>{f.label}{isReq(key) ? " *" : ""}</label>
                              <AddressPicker value={form} onPatch={(p) => setForm((ff) => ({ ...ff, ...p }))} testidPrefix="pb-addr" required={isReq(key)} />
                            </div>
                          );
                        }
                        if (f.type === "multiselect") {
                          const arr = Array.isArray(form[key]) ? form[key] : [];
                          return (
                            <div key={key} className="sm:col-span-2">
                              <label className={labelCls}>{f.label}{isReq(key) ? " *" : ""}</label>
                              <div className="grid grid-cols-2 gap-1.5">
                                {f.options.map((o) => (
                                  <label key={o} className="flex items-center gap-2 rounded-lg border border-slate-200 px-2 py-1.5 text-xs text-slate-600">
                                    <input type="checkbox" data-testid={`${f.testid}-opt`} checked={arr.includes(o)} onChange={(e) => set(key, e.target.checked ? [...arr, o] : arr.filter((x) => x !== o))} />
                                    {o}
                                  </label>
                                ))}
                              </div>
                            </div>
                          );
                        }
                        if (f.type === "consent") {
                          return (
                            <div key={key} className="sm:col-span-2 rounded-xl border border-pink-200 bg-pink-50 p-3">
                              <label className="flex items-start gap-2 text-xs text-slate-700">
                                <input type="checkbox" data-testid={f.testid} checked={!!form[key]} onChange={(e) => set(key, e.target.checked)} className="mt-0.5" />
                                <span>{CONSENT_TEXT}</span>
                              </label>
                            </div>
                          );
                        }
                        if (f.type === "textarea") {
                          return (
                            <div key={key} className="sm:col-span-2">
                              <label className={labelCls}>{f.label}{isReq(key) ? " *" : ""}</label>
                              <textarea data-testid={f.testid} rows={2} value={form[key] || ""} onChange={(e) => set(key, e.target.value)} className={inputCls} />
                            </div>
                          );
                        }
                        const age = (key === "tanggal_lahir" || key === "tanggal_lahir_bayi") ? calcAgeParts(form[key]) : null;
                        return (
                          <div key={key} className={key === "hpht" ? "sm:col-span-2" : ""}>
                            <label className={labelCls}>{f.label}{isReq(key) ? " *" : ""}</label>
                            {f.type === "select" ? (
                              <select data-testid={f.testid} value={form[key] || ""} onChange={(e) => set(key, e.target.value)} className={inputCls}>
                                <option value="">-- Pilih --</option>
                                {f.options.map((o) => <option key={o} value={o}>{o}</option>)}
                              </select>
                            ) : (
                              <input data-testid={f.testid} type={f.numeric ? "text" : f.type} inputMode={f.numeric ? "numeric" : undefined} maxLength={f.maxLength} min={f.type === "number" ? "0" : undefined} value={form[key] || ""} onChange={(e) => set(key, f.numeric ? e.target.value.replace(/\D/g, "").slice(0, f.maxLength || 32) : e.target.value)} className={inputCls} placeholder={f.placeholder || ""} />
                            )}
                            {age && <p data-testid={`${f.testid}-age`} className="mt-1 text-xs font-semibold text-emerald-600">Usia: {age.text}</p>}
                            {key === "hpht" && form.hpht && preg && (
                              <p data-testid="pb-uk-hpl" className="mt-1 text-xs font-semibold text-[#DB2777]">Usia Kehamilan: {preg.ukText} · HPL: {preg.hpl ? formatDateID(preg.hpl) : "-"}</p>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Homecare address */}
                {isHome && !effectiveFields.includes("alamat") && (
                  <div className="rounded-2xl border border-rose-100/70 bg-white p-5" data-testid="fields-homecare">
                    <h3 className="mb-4 font-heading text-base font-bold text-slate-800">Alamat Homecare</h3>
                    <AddressPicker value={form} onPatch={(p) => setForm((f) => ({ ...f, ...p }))} testidPrefix="pb-addr" />
                    <div className="mt-3 grid grid-cols-1 gap-4 sm:grid-cols-2">
                      <div><label className={labelCls}>Patokan / Catatan Lokasi</label><input data-testid="pb-patokan" value={form.patokan || ""} onChange={(e) => set("patokan", e.target.value)} className={inputCls} placeholder="cth. depan masjid" /></div>
                      <div><label className={labelCls}>Link Google Maps</label><input data-testid="pb-maps" value={form.maps_link || ""} onChange={(e) => set("maps_link", e.target.value)} className={inputCls} placeholder="https://maps.app..." /></div>
                    </div>
                  </div>
                )}

                {/* Schedule + notes */}
                <div className="rounded-2xl border border-rose-100/70 bg-white p-5">
                  <h3 className="mb-4 font-heading text-base font-bold text-slate-800">Jadwal Kunjungan</h3>
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    {showPetugas && (
                    <div>
                      <label className={labelCls}>Petugas / Bidan *</label>
                      {isHome && !form.kelurahan ? (
                        <p data-testid="pb-need-address" className="rounded-xl bg-slate-50 px-3 py-2.5 text-xs text-slate-400">Pilih alamat (hingga Kelurahan/Desa) terlebih dahulu untuk melihat petugas Homecare yang melayani wilayah Anda.</p>
                      ) : isHome && homecareStaff !== null && homecareStaff.length === 0 ? (
                        <p data-testid="pb-no-homecare-staff" className="rounded-xl bg-rose-50 px-3 py-2.5 text-xs font-medium text-rose-600">Saat ini belum ada petugas Homecare yang tersedia untuk wilayah Anda. Silakan hubungi Admin GBS untuk bantuan penjadwalan.</p>
                      ) : (
                        <div>
                          {(() => { const _pl = (isHome ? (homecareStaff || []) : (service?.allowed_petugas && service.allowed_petugas.length > 0 ? staffList.filter((s) => service.allowed_petugas.includes(s.nama)) : staffList)); return (
                            <div data-testid="pb-petugas-cards" className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                              {_pl.map((s) => { const sel = form.petugas_nama === s.nama; return (
                                <button type="button" key={s.id} data-testid={`pb-petugas-card-${s.id}`} onClick={() => set("petugas_nama", s.nama)} className={`flex items-center gap-3 rounded-2xl border p-3 text-left transition-all ${sel ? "border-[#EC4899] bg-[#FDF2F8] ring-2 ring-[#FBCFE8]" : "border-rose-100/70 bg-white hover:border-[#EC4899]"}`}>
                                  <EntityPhoto src={s.foto_file_id ? `${API}/public/staff/${s.id}/foto` : null} name={s.nama} rounded="rounded-full" className="h-12 w-12 shrink-0" />
                                  <div className="min-w-0 flex-1">
                                    <p className="font-heading text-sm font-bold text-slate-800">{s.nama}</p>
                                    {(s.jabatan || s.jenis) && <p className="text-xs text-slate-500">{s.jabatan || s.jenis}</p>}
                                    {s.masa_kerja_text && <p className="text-[11px] font-medium text-emerald-700">{s.masa_kerja_text}</p>}
                                    {s.pengalaman_text && <p className="text-[11px] font-medium text-sky-700">{s.pengalaman_text}</p>}
                                    {s.is_mitra && <p data-testid={`pb-mitra-badge-${s.id}`} className="mt-0.5 inline-flex items-center gap-1 rounded-full bg-violet-100 px-2 py-0.5 text-[10px] font-bold text-violet-700">Mitra MOG{s.kota_layanan ? ` — ${s.kota_layanan}` : ""}{s.tz_label ? ` · Waktu ${s.tz_label}` : ""}</p>}
                                  </div>
                                  <span className={`shrink-0 rounded-full px-2.5 py-1 text-xs font-bold ${sel ? "bg-[#EC4899] text-white" : "bg-slate-100 text-slate-500"}`}>{sel ? "Terpilih" : "Pilih"}</span>
                                </button>
                              ); })}
                            </div>
                          ); })()}
                          <select data-testid="pb-petugas" value={form.petugas_nama || ""} onChange={(e) => set("petugas_nama", e.target.value)} className="sr-only" aria-hidden="true" tabIndex={-1}>
                            <option value="">-- Pilih Petugas --</option>
                            {(isHome ? (homecareStaff || []) : (service?.allowed_petugas && service.allowed_petugas.length > 0 ? staffList.filter((s) => service.allowed_petugas.includes(s.nama)) : staffList)).map((s) => <option key={s.id} value={s.nama}>{s.nama}{s.jenis ? ` (${s.jenis})` : ""}</option>)}
                          </select>
                        </div>
                      )}
                    </div>
                    )}
                    {fixedMode && (
                      <div data-testid="pb-fixed-note" className="rounded-xl bg-[#EEF6FF] px-3 py-2.5 text-xs text-sky-700 sm:col-span-2">📅 {lokasi === ONLINE ? "Sesi konsultasi ditentukan oleh jadwal layanan." : "Jadwal kelas ditentukan oleh layanan."} {showPetugas ? "" : "Petugas akan ditugaskan otomatis."}</div>
                    )}
                    <div><label className={labelCls}>Tanggal Kunjungan *</label><input data-testid="pb-tanggal" type="date" required min={new Date().toISOString().slice(0, 10)} value={form.tanggal_kunjungan || ""} onChange={(e) => set("tanggal_kunjungan", e.target.value)} className={inputCls} /></div>
                    <div className="sm:col-span-2">
                      <label className={labelCls}>{practice ? "Jadwal Praktik" : "Jam Kunjungan Tersedia *"}</label>
                      {practice ? (
                        <div data-testid="pb-practice-note" className="rounded-xl bg-[#EEF6FF] px-3 py-2.5 text-xs text-sky-700">
                          🩺 Jadwal praktik {form.petugas_nama}: {practice.window}. Anda tidak perlu memilih jam — nomor antrean &amp; perkiraan jam ditentukan otomatis.
                          {practice.can_book === false && <span className="mt-1 block font-bold text-rose-600">Kuota sesi praktik pada tanggal ini sudah penuh. Silakan pilih tanggal lain.</span>}
                        </div>
                      ) : fixedMode ? (
                        !form.tanggal_kunjungan ? (
                          <p className="rounded-xl bg-slate-50 px-3 py-2.5 text-xs text-slate-400">Pilih tanggal untuk melihat sesi kelas yang tersedia.</p>
                        ) : svcSlots === "loading" ? (
                          <p className="rounded-xl bg-slate-50 px-3 py-2.5 text-xs text-slate-400">Memuat sesi...</p>
                        ) : !svcSlots || (svcSlots.sessions || []).length === 0 ? (
                          <p data-testid="pb-no-slots" className="rounded-xl bg-rose-50 px-3 py-2.5 text-xs text-rose-500">Tidak ada sesi untuk tanggal ini. Silakan pilih tanggal sesuai jadwal layanan.</p>
                        ) : (
                          <div className="flex flex-wrap gap-2" data-testid="pb-slots">
                            {svcSlots.sessions.map((ss) => {
                              const nearFull = !ss.full && ss.remaining <= 2;
                              return (
                              <button key={ss.time} type="button" disabled={ss.full} data-testid={`pb-slot-${ss.time}`} onClick={() => !ss.full && set("jam_kunjungan", ss.time)} className={`relative rounded-lg border px-3 py-2 text-sm font-semibold transition-colors ${ss.full ? "cursor-not-allowed border-slate-200 bg-slate-100 text-slate-400" : form.jam_kunjungan === ss.time ? "border-[#EC4899] bg-[#FCE7F3] text-[#DB2777]" : nearFull ? "border-amber-300 bg-amber-50 text-amber-700 hover:border-amber-400" : "border-slate-200 text-slate-600 hover:border-[#EC4899]"}`}>
                                {ss.time}{ss.end_time ? `–${ss.end_time}` : ""}
                                <span className={`ml-1 text-[10px] ${ss.full ? "text-rose-500" : nearFull ? "text-amber-600 font-bold" : "text-emerald-600"}`}>{ss.full ? (ss.reason === "no_staff" ? "PETUGAS BELUM TERSEDIA" : ss.reason === "facility" ? "FASILITAS TERPAKAI" : "PENUH") : `sisa ${ss.remaining}`}</span>
                                {nearFull && (
                                  <span data-testid={`pb-nearfull-${ss.time}`} className="ml-1 inline-flex items-center gap-0.5 rounded-full bg-amber-500 px-1.5 py-0.5 text-[9px] font-extrabold uppercase tracking-wide text-white"><Warning size={10} weight="fill" /> Hampir Penuh</span>
                                )}
                              </button>
                              );
                            })}
                          </div>
                        )
                      ) : !form.petugas_nama || !form.tanggal_kunjungan ? (
                        <p className="rounded-xl bg-slate-50 px-3 py-2.5 text-xs text-slate-400">Pilih petugas & tanggal dulu untuk melihat jam yang tersedia.</p>
                      ) : avail === "loading" ? (
                        <p className="rounded-xl bg-slate-50 px-3 py-2.5 text-xs text-slate-400">Memuat jam tersedia...</p>
                      ) : avail && avail.available.length === 0 ? (
                        <div>
                          <p data-testid="pb-no-slots" className="rounded-xl bg-rose-50 px-3 py-2.5 text-xs text-rose-500">Tidak ada jam tersedia untuk petugas & tanggal ini. Silakan pilih tanggal atau petugas lain.</p>
                          <NearestSlots serviceId={service?.id} date={form.tanggal_kunjungan} petugas={form.petugas_nama} lokasi={lokasi} clinicWa={settings?.clinic_wa}
                            candidates={(isHome ? (homecareStaff || []) : (service?.allowed_petugas && service.allowed_petugas.length > 0 ? staffList.filter((s) => service.allowed_petugas.includes(s.nama)) : staffList)).map((s) => s.nama)}
                            onPick={(s) => { pendingJam.current = s.jam; setForm((f) => ({ ...f, petugas_nama: s.petugas_nama, tanggal_kunjungan: s.tanggal, jam_kunjungan: "" })); }} />
                        </div>
                      ) : (
                        <div className="flex flex-wrap gap-2" data-testid="pb-slots">
                          {avail && avail.available.map((t) => (
                            <button key={t} type="button" data-testid={`pb-slot-${t}`} onClick={() => set("jam_kunjungan", t)} className={`rounded-lg border px-3 py-2 text-sm font-semibold transition-colors ${form.jam_kunjungan === t ? "border-[#EC4899] bg-[#FCE7F3] text-[#DB2777]" : "border-slate-200 text-slate-600 hover:border-[#EC4899]"}`}>{t}</button>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="sm:col-span-2"><label className={labelCls}>Keluhan / Catatan</label><textarea data-testid="pb-keluhan" rows={2} value={form.keluhan || ""} onChange={(e) => set("keluhan", e.target.value)} className={inputCls} placeholder="Opsional" /></div>
                  </div>
                </div>

                {error && <p data-testid="pb-error" className="rounded-lg bg-rose-50 px-4 py-3 text-sm text-rose-600">{error}</p>}

                <PrivacyConsentBox checked={privacyOk} onChange={setPrivacyOk} testid="pb-privacy-consent" />
                <div className="flex justify-end">
                  <button data-testid="pb-submit" type="submit" disabled={!privacyOk || submitting || (practice && practice.can_book === false)} className="rounded-xl bg-[#EC4899] px-8 py-3 text-sm font-bold text-white shadow-sm transition-all hover:bg-[#DB2777] active:scale-95 disabled:opacity-60">{submitting ? "Mengirim..." : ((service?.payment_mode || "none") !== "none" ? "Lanjutkan" : "Kirim Reservasi")}</button>
                </div>
              </form>
            )}
          </>
        )}
      </main>
      )}

      {showKetentuan && service && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="ketentuan-modal">
          <div className="max-h-[88vh] w-full max-w-lg overflow-y-auto rounded-2xl bg-white p-5 shadow-xl">
            <h2 className="font-heading text-xl font-extrabold text-slate-800">Ketentuan Reservasi & Pembayaran</h2>
            <div className="mt-3 space-y-1.5 rounded-xl bg-[#FAF7FF] p-3 text-sm">
              <div className="flex justify-between"><span className="text-slate-500">Layanan</span><span className="font-bold text-slate-800">{service.nama}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Tanggal & Jam</span><span className="font-semibold text-slate-700">{formatDateID(form.tanggal_kunjungan)} · {form.jam_kunjungan}{(() => { const _ss = (homecareStaff || []).find((x) => x.nama === form.petugas_nama); return _ss && _ss.is_mitra && _ss.tz_label ? ` ${_ss.tz_label}` : ""; })()}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Total Harga</span><span className="font-bold text-slate-800">{formatRp(previewTotal())}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Jenis Pembayaran</span><span className="font-bold text-[#DB2777]">{service.payment_mode === "dp" ? "Wajib DP" : service.payment_mode === "full" ? "Wajib Lunas" : "Pembayaran Opsional"}</span></div>
              {service.payment_mode === "dp" && <div className="flex justify-between"><span className="text-slate-500">DP Wajib</span><span className="font-bold text-[#DB2777]">{formatRp(previewDp())}</span></div>}
              {service.payment_mode === "dp" && <div className="flex justify-between"><span className="text-slate-500">Sisa Tagihan</span><span className="text-slate-700">{formatRp(previewTotal() - previewDp())}</span></div>}
            </div>
            <div className="mt-3 space-y-2 text-sm">
              {[["Ketentuan Pembatalan", settings?.policy_cancel], ["Ketentuan Reschedule", settings?.policy_reschedule], ["Ketentuan Refund", settings?.policy_refund], ["Ketentuan No Show", settings?.policy_noshow]].map(([t, txt]) => (
                <div key={t}><p className="text-xs font-bold text-slate-700">{t}</p><p className="text-xs text-slate-500">{txt || "-"}</p></div>
              ))}
            </div>
            <label className="mt-4 flex items-start gap-2 rounded-xl bg-amber-50 p-3 text-xs text-slate-700">
              <input data-testid="ketentuan-checkbox" type="checkbox" checked={policyOk} onChange={(e) => setPolicyOk(e.target.checked)} className="mt-0.5" />
              <span>Saya telah membaca, memahami, dan menyetujui Ketentuan Reservasi, Pembayaran, Pembatalan, Reschedule, dan Refund yang berlaku untuk layanan ini.</span>
            </label>
            {error && <p className="mt-2 text-sm text-rose-600">{error}</p>}
            <div className="mt-4 flex justify-end gap-2">
              <button data-testid="ketentuan-cancel" onClick={() => setShowKetentuan(false)} className="rounded-xl px-4 py-2.5 text-sm font-semibold text-slate-500">Batal</button>
              <button data-testid="ketentuan-continue" disabled={!policyOk || submitting} onClick={() => proceed(true)} className="rounded-xl bg-[#EC4899] px-6 py-2.5 text-sm font-bold text-white hover:bg-[#DB2777] disabled:opacity-50">{submitting ? "Memproses..." : "Lanjut ke Pembayaran"}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


export function PaymentPanel({ resId, payInfo, waInfo, payToken }) {
  const [methods, setMethods] = useState([]);
  const [amount, setAmount] = useState(payInfo.mode === "dp" ? payInfo.dp_required : payInfo.total);
  const [method, setMethod] = useState("Transfer Bank");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [ok, setOk] = useState(false);
  const [paidAmt, setPaidAmt] = useState(null);
  const [status, setStatus] = useState(null);
  const [left, setLeft] = useState("");
  const [mtCfg, setMtCfg] = useState(null);
  const [mtBusy, setMtBusy] = useState(false);
  const [mtErr, setMtErr] = useState("");
  const [clinicWa, setClinicWa] = useState(waInfo?.clinic_wa || "");
  useEffect(() => {
    if (!waInfo?.clinic_wa) { axios.get(`${API}/public/settings`).then((r) => setClinicWa(r.data?.clinic_wa || "")).catch(() => {}); }
  }, [waInfo]);

  const paidWaUrl = () => {
    if (!waInfo?.clinic_wa) return "";
    const msg = `Halo Admin GBS Clinic, saya ${waInfo.nama} telah melakukan reservasi dan mengirim bukti pembayaran:\nLayanan: ${waInfo.layanan}\nTanggal: ${waInfo.tanggal}\nJam: ${waInfo.jam}\nNominal: ${formatRp(paidAmt ?? amount)}\nStatus: Menunggu Verifikasi\nMohon konfirmasinya. Terima kasih.`;
    return waLink(waInfo.clinic_wa, msg);
  };

  useEffect(() => { axios.get(`${API}/public/payment-methods`).then((r) => setMethods(r.data)).catch(() => {}); axios.get(`${API}/public/payments/midtrans/config`).then((r) => setMtCfg(r.data)).catch(() => {}); }, []);
  useEffect(() => {
    const url = payToken ? `${API}/public/pay/${payToken}` : `${API}/public/reservation-status/${resId}`;
    const poll = () => axios.get(url).then((r) => setStatus(r.data)).catch(() => {});
    poll();
    const t = setInterval(poll, 8000);
    return () => clearInterval(t);
  }, [resId, payToken]);
  useEffect(() => {
    if (!payInfo.deadline) return;
    const tick = () => {
      const ms = new Date(payInfo.deadline) - new Date();
      if (ms <= 0) { setLeft("Batas waktu habis"); return; }
      const m = Math.floor(ms / 60000), s = Math.floor((ms % 60000) / 1000);
      setLeft(`${m}m ${s}s`);
    };
    tick();
    const t = setInterval(tick, 1000);
    return () => clearInterval(t);
  }, [payInfo.deadline]);

  const upload = async () => {
    setErr("");
    if (!file) return setErr("Pilih file bukti pembayaran (JPG/PNG/PDF)");
    if (!amount || Number(amount) <= 0) return setErr("Nominal tidak valid");
    setBusy(true);
    const fd = new FormData();
    if (!payToken) fd.append("reservation_id", resId);
    fd.append("amount", amount);
    fd.append("method", method);
    fd.append("file", file);
    try {
      const upUrl = payToken ? `${API}/public/pay/${payToken}/upload` : `${API}/public/payments/upload`;
      await axios.post(upUrl, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setOk(true);
      setPaidAmt(amount);
      setStatus((s) => ({ ...(s || {}), payment_status: "Menunggu Verifikasi" }));
    } catch (e) {
      setErr(e?.response?.data?.detail || "Gagal mengunggah bukti.");
    } finally { setBusy(false); }
  };

  const payMidtrans = async () => {
    if (mtBusy) return;
    setErr(""); setMtErr(""); setMtBusy(true);
    let tokenOk = false;
    try {
      const mt = await import("@/lib/midtrans");
      const res = payToken
        ? await mt.payWithMidtransReservationTokenPublic({ token: payToken })
        : await mt.payWithMidtransReservationPublic({ rid: resId });
      tokenOk = true;
      if (res.status === "error") setErr("Pembayaran gagal / ditolak. Anda dapat mencoba lagi.");
      const reconcileUrl = payToken ? `${API}/public/pay/${payToken}/reconcile` : `${API}/public/payments/midtrans/reservation/${resId}/reconcile`;
      try { await axios.post(reconcileUrl); } catch (_) { /* best-effort */ }
      const statusUrl = payToken ? `${API}/public/pay/${payToken}` : `${API}/public/reservation-status/${resId}`;
      const r = await axios.get(statusUrl);
      setStatus(r.data);
    } catch (e) {
      const m = e?.response?.data?.detail || e.message || "Gagal memproses pembayaran Midtrans.";
      if (tokenOk) setErr(m);
      else setMtErr(`Pembayaran online belum bisa dibuka: ${m}`);
    } finally { setMtBusy(false); }
  };

  const ps = status?.payment_status || payInfo.payment_status;
  const confirmed = status && (ps === "DP Terbayar" || ps === "Lunas" || status.status === "Dikonfirmasi");

  return (
    <div data-testid="payment-panel" className="mt-6 rounded-2xl border border-[#E0D2FF] bg-white p-5 text-left">
      <h2 className="font-heading text-lg font-bold text-slate-800">Rincian Pembayaran</h2>
      <div className="mt-3 space-y-1.5 text-sm">
        <div className="flex justify-between"><span className="text-slate-500">Total Layanan</span><span className="font-bold text-slate-800">{formatRp(payInfo.total)}</span></div>
        {payInfo.mode === "dp" && <div className="flex justify-between"><span className="text-slate-500">DP Wajib</span><span className="font-bold text-[#DB2777]">{formatRp(payInfo.dp_required)}</span></div>}
        {payInfo.mode === "dp" && <div className="flex justify-between"><span className="text-slate-500">Sisa Pembayaran</span><span className="text-slate-700">{formatRp(payInfo.total - payInfo.dp_required)}</span></div>}
        {status && <div className="flex justify-between"><span className="text-slate-500">Sudah Dibayar</span><span className="text-emerald-600">{formatRp(status.total_dibayar || 0)}</span></div>}
      </div>

      {payInfo.deadline && !confirmed && !ok && ps !== "Menunggu Verifikasi" && (
        <p data-testid="payment-countdown" className="mt-3 rounded-lg bg-amber-50 px-3 py-2 text-sm font-semibold text-amber-700">⏳ Batas pembayaran: {left}</p>
      )}

      {confirmed ? (
        <div data-testid="payment-confirmed" className="mt-4 space-y-3">
          <p className="rounded-lg bg-emerald-50 px-3 py-2 text-sm font-semibold text-emerald-700">✓ Pembayaran diverifikasi. Reservasi Anda terkonfirmasi.</p>
          {waInfo?.clinic_wa && <a data-testid="pay-wa-confirm" href={paidWaUrl()} target="_blank" rel="noreferrer" className="flex items-center justify-center gap-2 rounded-xl bg-[#25D366] px-6 py-2.5 text-sm font-bold text-white hover:bg-[#20b858]"><WhatsappLogo size={18} weight="fill" /> Konfirmasi via WhatsApp</a>}
        </div>
      ) : ps === "Menunggu Verifikasi" || ok ? (
        <div data-testid="payment-pending" className="mt-4 space-y-3">
          <p className="rounded-lg bg-sky-50 px-3 py-2 text-sm font-semibold text-sky-700">Bukti pembayaran telah diterima dan sedang menunggu verifikasi Admin.</p>
          {waInfo?.clinic_wa && <a data-testid="pay-wa-confirm" href={paidWaUrl()} target="_blank" rel="noreferrer" className="flex items-center justify-center gap-2 rounded-xl bg-[#25D366] px-6 py-2.5 text-sm font-bold text-white hover:bg-[#20b858]"><WhatsappLogo size={18} weight="fill" /> Konfirmasi via WhatsApp</a>}
        </div>
      ) : (
        <>
          {(() => {
            const mode = status?.payment_mode || payInfo.mode;
            const paidAmt2 = Math.round(status?.total_dibayar || 0);
            const sisa = Math.round(status?.sisa_tagihan ?? (payInfo.total - paidAmt2));
            const dpReq = Math.round(status?.dp_required ?? payInfo.dp_required ?? 0);
            const mtAmount = mode === "dp" ? (Math.max(0, dpReq - paidAmt2) || sisa) : sisa;
            const canMt = mtCfg?.configured && mode !== "none" && sisa > 0 && !status?.expired && status?.status !== "Batal";
            if (!canMt) return null;
            return (
              <div data-testid="midtrans-public-block" className="mt-4 rounded-xl border-2 border-[#7C4DE0] bg-[#F5F0FF] p-4">
                <p className="text-sm font-extrabold text-[#6A38C7]">Bayar Otomatis via Midtrans</p>
                {!mtCfg?.is_production && <p className="mt-1 rounded bg-amber-50 px-2 py-1 text-[11px] font-semibold text-amber-700" data-testid="mt-public-sandbox">Mode uji (SANDBOX)</p>}
                <p className="mt-2 text-sm text-slate-600">Nominal: <span className="font-extrabold text-[#6A38C7]" data-testid="mt-public-amount">{formatRp(mtAmount)}</span>{mode === "dp" ? " (DP)" : " (Lunas)"}</p>
                <button data-testid="mt-public-pay" onClick={payMidtrans} disabled={mtBusy} className="mt-2 w-full rounded-xl bg-[#7C4DE0] py-2.5 text-sm font-bold text-white hover:bg-[#6A38C7] disabled:opacity-60">{mtBusy ? "Memproses…" : "Bayar Sekarang via Midtrans"}</button>
                {mtErr && (
                  <div data-testid="mt-public-error" role="alert" className="mt-3 space-y-2 rounded-lg border border-rose-200 bg-rose-50 p-3">
                    <p className="text-sm font-semibold text-rose-700">{mtErr}</p>
                    {clinicWa && <a data-testid="mt-public-error-wa" href={waLink(clinicWa, `Halo Admin GBS Clinic, saya tidak bisa membayar reservasi via Midtrans. Pesan: "${mtErr}". Mohon dibantu.`)} target="_blank" rel="noreferrer" className="flex items-center justify-center gap-2 rounded-xl bg-[#25D366] py-2.5 text-sm font-bold text-white hover:bg-[#20b858]"><WhatsappLogo size={18} weight="fill" /> Hubungi Admin via WhatsApp</a>}
                  </div>
                )}
                <p className="mt-2 text-center text-[11px] text-slate-400">Kartu kredit, e-wallet, VA, QRIS — tanpa upload bukti.</p>
              </div>
            );
          })()}

          <div className="mt-4 space-y-2">
            <p className="text-sm font-semibold text-slate-600">Atau Transfer Manual / QRIS dan Upload Bukti:</p>
            {methods.length === 0 && <p className="text-xs text-slate-400">Belum ada metode pembayaran aktif. Silakan hubungi admin.</p>}
            {methods.map((m) => (
              <div key={m.id} className="rounded-xl border border-slate-200 p-3 text-sm">
                {m.type === "qris" ? (
                  <div><p className="font-bold text-slate-700">QRIS · {m.merchant_name}</p>{m.qris_file_id && <img alt="QRIS" src={`${API}/public/payment-methods/${m.id}/qris`} className="mt-2 h-40 w-40 rounded-lg border object-contain" />}</div>
                ) : (
                  <div><p className="font-bold text-slate-700">{m.nama_bank}</p><p className="text-slate-600">{m.no_rekening} · a.n. {m.atas_nama}</p></div>
                )}
              </div>
            ))}
          </div>

          <div className="mt-4 space-y-2">
            <div className="grid grid-cols-2 gap-2">
              <div><label className="mb-1 block text-xs font-semibold text-slate-500">Nominal Dibayar</label><input data-testid="pay-amount" type="number" value={amount} onChange={(e) => setAmount(e.target.value)} className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm" /></div>
              <div><label className="mb-1 block text-xs font-semibold text-slate-500">Metode</label>
                <select data-testid="pay-method" value={method} onChange={(e) => setMethod(e.target.value)} className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm">
                  <option>Transfer Bank</option><option>QRIS</option><option>Lainnya</option>
                </select>
              </div>
            </div>
            <div><label className="mb-1 block text-xs font-semibold text-slate-500">Bukti Pembayaran (JPG/PNG/PDF, maks 5MB)</label>
              <input data-testid="pay-file" type="file" accept=".jpg,.jpeg,.png,.pdf" onChange={(e) => setFile(e.target.files[0])} className="w-full text-sm" />
            </div>
            {err && <p className="text-sm font-semibold text-rose-600">{err}</p>}
            <button data-testid="pay-upload" onClick={upload} disabled={busy} className="w-full rounded-xl bg-[#EC4899] py-2.5 text-sm font-bold text-white hover:bg-[#DB2777] disabled:opacity-60">{busy ? "Mengunggah..." : "Upload Bukti Pembayaran"}</button>
          </div>
        </>
      )}
    </div>
  );
}
